import { Component, useEffect, useRef, useState, Suspense } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { isMobile } from 'Services/Util';

import graphql from 'babel-plugin-relay/macro';
import { useQueryLoader, usePreloadedQuery } from 'react-relay';

import LocalGroceryStoreRoundedIcon from '@mui/icons-material/LocalGroceryStoreRounded';

import {
  PARTNER_KEYS,
  PARTNER_LABEL,
  PARTNER_LOGO
} from 'Routes/ScanData/partnerConfig';
import { getScanDataProgram } from 'Routes/ScanData/partnerDisplay';

const CLOSE_DELAY_MS = 150;

class MenuErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('ScanDataNavItem menu query failed:', error);
    }
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

function ScanDataMenuItems({ queryRef, onItemClick }) {
  const { viewer } = usePreloadedQuery(
    graphql`
      query ScanDataNavItemQuery {
        viewer {
          partnerRegistry
          storeChain {
            reportAccounts(
              first: 100
              filters: { statuses: [LIVE], useFilters: true }
            ) {
              edges {
                node {
                  id
                  partner
                }
              }
            }
          }
        }
      }
    `,
    queryRef
  );
  const partnerRegistry = viewer?.partnerRegistry;
  const accountEdges = viewer?.storeChain?.reportAccounts?.edges || [];
  const enrolledPartners = new Set(
    accountEdges.map(({ node }) => node?.partner).filter(Boolean)
  );
  const enrolledKeys = PARTNER_KEYS.filter(key => enrolledPartners.has(key));

  if (enrolledKeys.length === 0) {
    return (
      <div className="px-4 py-2 text-sm text-gray-500">
        No enrolled programs
      </div>
    );
  }

  return enrolledKeys.map(key => {
    const program = getScanDataProgram(partnerRegistry, key);
    const label = PARTNER_LABEL[key] || key;
    const programName = program?.programName || `${label} Scan Data`;
    const logoUrl = program?.logoUrl || PARTNER_LOGO[key];
    return (
      <Link
        key={key}
        to={`/scandata/${key.toLowerCase()}`}
        onClick={onItemClick}
        className="flex items-center gap-3 px-4 py-2 text-sm text-indigo-800 hover:bg-gray-50"
      >
        <div className="flex items-center justify-center h-8 w-8 shrink-0 p-1 border rounded border-slate-200 overflow-hidden bg-white">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${label} logo`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <LocalGroceryStoreRoundedIcon
              style={{ fontSize: '20px', color: '#EBEDEE' }}
            />
          )}
        </div>
        <span className="truncate">{programName}</span>
      </Link>
    );
  });
}

function PlaceholderItems({ onItemClick }) {
  return PARTNER_KEYS.map(key => {
    const label = PARTNER_LABEL[key] || key;
    const logoUrl = PARTNER_LOGO[key];
    return (
      <Link
        key={key}
        to={`/scandata/${key.toLowerCase()}`}
        onClick={onItemClick}
        className="flex items-center gap-3 px-4 py-2 text-sm text-indigo-800 hover:bg-gray-50"
      >
        <div className="flex items-center justify-center h-8 w-8 shrink-0 p-1 border rounded border-slate-200 overflow-hidden bg-white">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${label} logo`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <LocalGroceryStoreRoundedIcon
              style={{ fontSize: '20px', color: '#EBEDEE' }}
            />
          )}
        </div>
        <span className="truncate">{label} Scan Data</span>
      </Link>
    );
  });
}

function LoadingSkeleton() {
  return [0, 1, 2].map(i => (
    <div key={i} className="flex items-center gap-3 px-4 py-2">
      <div className="h-8 w-8 shrink-0 rounded bg-gray-200 animate-pulse" />
      <div className="h-3 w-28 rounded bg-gray-200 animate-pulse" />
    </div>
  ));
}

export default function ScanDataNavItem() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const mobile = isMobile();
  const [queryRef, loadQuery] = useQueryLoader(
    require('./__generated__/ScanDataNavItemQuery.graphql')
  );

  useEffect(() => {
    if (open && queryRef == null) {
      loadQuery({}, { fetchPolicy: 'store-or-network' });
    }
  }, [open, queryRef, loadQuery]);

  const handleOpen = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const handleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  if (mobile) {
    return (
      <NavLink
        to="/scandata"
        className="Navigation--link px-6"
        activeClassName="active"
      >
        Scan Data
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
    >
      <NavLink
        to="/scandata"
        className="Navigation--link px-6"
        activeClassName="active"
      >
        Scan Data
      </NavLink>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white shadow-2 rounded py-2 z-10"
          style={{ width: 210 }}
        >
          <MenuErrorBoundary
            fallback={<PlaceholderItems onItemClick={() => setOpen(false)} />}
          >
            <Suspense fallback={<LoadingSkeleton />}>
              {queryRef != null ? (
                <ScanDataMenuItems
                  queryRef={queryRef}
                  onItemClick={() => setOpen(false)}
                />
              ) : (
                <LoadingSkeleton />
              )}
            </Suspense>
          </MenuErrorBoundary>
        </div>
      )}
    </div>
  );
}
